// OpenProject — renders at /projects/:id.
// If the project is already loaded in the store (navigated from Dashboard),
// renders ProjectEditor directly. If the store is empty (page refresh / direct URL),
// fetches the project from the API first.

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProjectStore } from '../store/projectStore';
import { useUiStore } from '../store/uiStore';
import { ProjectEditor } from './ProjectEditor';
import type { Project } from '../types';

export function OpenProject() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const project = useProjectStore(st => st.project);
  const setProject = useProjectStore(st => st.setProject);
  const setCurrentStep = useUiStore(st => st.setCurrentStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) { navigate('/'); return; }
    // Project is already loaded (came from Dashboard).
    if (project?.id === id) return;

    // Cold load — page refresh or direct URL.
    setLoading(true);
    setError(null);
    fetch(`/api/projects/${id}`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<Project>;
      })
      .then(data => {
        const activeScenarioId =
          data.activeScenarioId ??
          (data.scenarios.find(s => s.isBaseline) ?? data.scenarios[0])?.id;
        setProject({ ...data, activeScenarioId });
        setCurrentStep(1);
      })
      .catch(() => {
        setError('Could not load project. Returning to dashboard…');
        setTimeout(() => navigate('/'), 2000);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <p style={{ color: '#94a3b8', padding: '40px 24px', fontSize: '14px' }}>
        Loading project…
      </p>
    );
  }

  if (error) {
    return (
      <p style={{ color: '#ef4444', padding: '40px 24px', fontSize: '14px' }}>
        {error}
      </p>
    );
  }

  if (!project || project.id !== id) return null;

  return <ProjectEditor />;
}
